import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import ScatteredImg from '../images/dubi/scattered.png';
import ManualImg from '../images/dubi/manual.png';
import LackImg from '../images/dubi/lack.png';

const PROBLEMS = [
  {
    label: 'Feedback lived in silos',
    body: 'Cross-team analysis required manual stitching that took days — if it happened at all.',
    image: ScatteredImg,
    accent: '#cef5ce',
    textColor: '#0f2e0f',
    rotate: -4,
    top: '20px',
    left: '5%',
    delay: 0,
  },
  {
    label: 'Setup killed momentum',
    body: 'A single campaign required manual CSV uploads, custom field mapping, and IT dependency, so teams skipped surveys entirely.',
    image: ManualImg,
    accent: '#1e3d2a',
    textColor: '#ffffff',
    rotate: 2,
    top: '135px',
    left: 'calc(5% + 24px)',
    delay: 160,
  },
  {
    label: 'Insights arrived too late',
    body: "Surveys weren't wired to journeys, so product decisions were made on instinct — not evidence.",
    image: LackImg,
    accent: '#fde68a',
    textColor: '#2a2200',
    rotate: -2,
    top: '250px',
    left: 'calc(5% + 10px)',
    delay: 320,
  },
];

/* ── Layout ───────────────────────────────────────────────── */

const Outer = styled.div`
  margin-top: 32px;
`;

const SectionWrap = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
  background: var(--color-surface);
  border-radius: 18px;
  padding: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 32px;
  }
`;

/* ── Left: stacked image cards ───────────────────────────── */

const ImagesCol = styled.div`
  flex: 0 0 42%;
  position: relative;
  height: 390px;

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
  }
`;

const CardWrap = styled.div`
  position: absolute;
  width: 80%;
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  z-index: ${({ $index }) => $index};

  transition:
    opacity   0.65s cubic-bezier(0.34, 1.56, 0.64, 1) ${({ $delay }) => $delay}ms,
    transform 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) ${({ $delay }) => $delay}ms,
    box-shadow 0.3s ease;

  ${({ $visible, $rotate }) =>
    $visible
      ? css`
          opacity: 1;
          transform: rotate(${$rotate}deg);
        `
      : css`
          opacity: 0;
          transform: rotate(${$rotate}deg) translateY(-50px);
        `}

  &:hover {
    transform: rotate(0deg) translateY(-10px) scale(1.04) !important;
    z-index: 20 !important;
  }

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    max-width: 100%;
    transform: none !important;
    opacity: 1 !important;
    border-radius: 16px;

    &:hover {
      transform: translateY(-4px) scale(1.01) !important;
    }
  }
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const CardLabel = styled.div`
  background: ${({ $accent }) => $accent};
  color: ${({ $textColor }) => $textColor};
  padding: 14px 18px;
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  line-height: 1.3;
`;

/* ── Right: text list ────────────────────────────────────── */

const ListCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ListItem = styled.div`
  background: var(--color-bg);
  border-radius: 12px;
  padding: 20px;
`;

const ItemLabel = styled.h4`
  margin-bottom: 8px;
`;

const ItemBody = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 400;
  line-height: 160%;
  opacity: 0.72;
  margin: 0;
`;

/* ── Component ───────────────────────────────────────────── */

const ProblemSection = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Outer ref={ref}>
      <SectionWrap>
      <ImagesCol>
        {PROBLEMS.map(({ label, image, accent, textColor, rotate, top, left, delay }, i) => (
          <CardWrap
            key={label}
            $visible={visible}
            $rotate={rotate}
            $top={top}
            $left={left}
            $delay={delay}
            $index={i + 1}
          >
            <CardImage src={image} alt={label} />
          </CardWrap>
        ))}
      </ImagesCol>

      <ListCol>
        {PROBLEMS.map(({ label, body }) => (
          <ListItem key={label}>
            <ItemLabel>{label}</ItemLabel>
            <ItemBody>{body}</ItemBody>
          </ListItem>
        ))}
      </ListCol>
      </SectionWrap>
    </Outer>
  );
};

export default ProblemSection;
